import { useContext, useEffect, useRef } from 'react';

import { getIncodeVerification } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { authorizationHeader } from '../../vehicleCollection/services/apiClient';
import { INCODE_POLLING_ERRORS, INCODE_POLLING_STATUS } from '../DocumentCapture.helpers';

type PollingOptions = {
  enquiryId: number;
  intervalMs?: number;
  maxAttempts?: number;
  onError: (error: any) => void;
  onSuccess: (data: any) => void;
};

export const useGetVerificationStatus = ({
  enquiryId,
  intervalMs = 5000,
  maxAttempts = 6,
  onError,
  onSuccess,
}: PollingOptions) => {
  const { userState: user } = useContext(UserContext);
  const authToken = user?.auth_token;

  const savedCallbacks = useRef({ onError, onSuccess });

  useEffect(() => {
    savedCallbacks.current = { onError, onSuccess };
  }, [onError, onSuccess]);

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const attemptPoll = () => {
      if (cancelled || !authToken || !enquiryId) {
        return;
      }

      getIncodeVerification(
        authorizationHeader(authToken),
        { enquiryId, type: 'seller' },
        abortController,
      )
        .then((response) => {
          if (cancelled) {
            return;
          }

          attempts += 1;
          const status = response?.data?.[0]?.verificationStatus;

          if (status === INCODE_POLLING_STATUS.COMPLETED) {
            savedCallbacks.current.onSuccess(response);
          } else if (status === INCODE_POLLING_STATUS.FAILED) {
            const error = new Error('Verification failed');
            error.name = INCODE_POLLING_ERRORS.FAILED;
            savedCallbacks.current.onError(error);
          } else if (attempts < maxAttempts) {
            timeout = setTimeout(attemptPoll, intervalMs);
          } else {
            const error = new Error('Max attempts reached');
            error.name = INCODE_POLLING_ERRORS.TIMEOUT;
            savedCallbacks.current.onError(error);
          }
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          if (err.name === 'AbortError') {
            return;
          }

          savedCallbacks.current.onError(err);
        });
    };

    attemptPoll();

    return () => {
      cancelled = true;
      if (timeout) {
        clearTimeout(timeout);
      }
      abortController.abort();
    };
  }, [enquiryId, intervalMs, maxAttempts, authToken]);
};
